import React from "react"; // No need to import { Component }
import { Card, Divider, Grid, Header, Icon, Table } from "semantic-ui-react"; // Added Icon import
import "../../node_modules/react-vis/dist/style.css";
import ChartUtils from "../charts/ChartUtils";
import "../css/home.css"; // Ensure you add the CSS classes here
import Utils from "../util/Utils";
import { isMobile } from "react-device-detect";

const renderTableYearHeaders = (dataSource, yearDataKey) => {
  return Object.keys(dataSource).map((index) => {
    const dataItem = dataSource[index];
    const year = dataItem?.[yearDataKey]?.year;
    if (!year) return null;
    const headerLabel = year.substring(0, 4);
    return <Table.HeaderCell key={index}>{headerLabel}</Table.HeaderCell>;
  });
};

const renderDataCells = (
  dataSource,
  dataKey,
  format_function,
  valuePath,
  params
) => {
  return Object.keys(dataSource).map((index) => {
    const cellData = dataSource[index]?.[dataKey]?.[valuePath];
    const cellElement = Utils.TableCell(cellData, format_function, ...params);
    return React.cloneElement(cellElement, { key: index });
  });
};

const epsGrowthRowsConfig = [
  {
    label: "EPS", // Label for the first cell in the row
    dataKey: "EPS", // Key used to access data in incomeStmtValues[index]
    valuePath: "value", // Path under dataKey to get the specific value
    format: Utils.format_currency_short, // Formatting function (or Utils.format_number depending on desired output)
    params: [0, 1], // Additional params for Utils.TableCell (adjust if needed based on original code [0, 1])
    trendChartKey: "EPS", // Key for getXyDataFromValues for the tiny chart
    trendChartDataKey: "Value", // Data key for the tiny chart
  },
  {
    label: "YoY Growth",
    dataKey: "EPS", // Use "EPS" data for growth calculation
    valuePath: "yoyGrowth", // Use yoyGrowth property
    format: Utils.format_percent,
    params: [0.1, 1], // Additional params for Utils.TableCell (adjust if needed)
    trendChartKey: "EPS", // Trend chart is based on EPS data itself
    trendChartDataKey: "Growth", // Data key for the tiny chart
  },
];

const EPSGrowthCheck = (props) => {
  const incomeYValues = props.stockValues.incomeStmtValues.slice(
    isMobile ? -3 : -5
  );

  return (
    <>
      <Divider horizontal>
        <Header as="h4">
          <Icon size="large" name="chart line" />
          Is EPS positive and growing consistently ?
        </Header>
      </Divider>
      <br />

      <Grid stackable centered>
        <Grid.Row columns={{ mobile: 1, tablet: 2, computer: 3 }}>
          <Grid.Column width={5}>
            <div className="chart-container">
              {ChartUtils.renderGrowthCard(
                "Past EPS", // Title for the growth card
                ChartUtils.getXyDataFromValues(
                  props.stockValues.incomeStmtValues, // Use income statement data
                  "EPS" // Use EPS data key
                ),
                Utils.format_number // Formatting for the card value
              )}
            </div>

            <div className="chart-container margin-top-1em">
              <Card raised>
                <Card.Content>
                  <Card.Header textAlign="center">
                    Future EPS Growth
                  </Card.Header>
                  <Card.Description>
                    <Table unstackable compact="very">
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell>
                            Analyst's Consensus Estimate Growth
                          </Table.Cell>
                          {Utils.TableCell(
                            props.stockValues.compProfile?.ePSnext5Y, // Safely access value
                            Utils.format_none,
                            10,
                            1,
                            "%" // Pass "%" as an extra argument for the unit
                          )}
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </Card.Description>
                </Card.Content>
              </Card>
            </div>
          </Grid.Column>

          <Grid.Column width={6}>
            <div style={{ padding: "0 0.5rem" }}>
              <Card fluid raised>
                <Card.Content>
                  <Card.Header textAlign="center">
                    Past Years Values
                    <Header.Subheader style={{ marginTop: "0.25em" }}>
                      (10 to 15% growth is ideal)
                    </Header.Subheader>
                  </Card.Header>
                  <Card.Description>
                    <Table
                      size="small"
                      unstackable
                      celled
                      className="table-margin-auto" // Use CSS class for margin
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Year</Table.HeaderCell>
                          {renderTableYearHeaders(
                            incomeYValues, // Adjusted to show last 3 years for mobile
                            "EPS"
                          )}
                          <Table.HeaderCell>Trend</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {epsGrowthRowsConfig.map((rowConfig) => (
                          <Table.Row key={rowConfig.label}>
                            <Table.Cell>{rowConfig.label}</Table.Cell>
                            {renderDataCells(
                              incomeYValues, // Pass the full data object
                              rowConfig.dataKey, // Pass the data key (e.g., "EPS")
                              rowConfig.format, // Pass the format function
                              rowConfig.valuePath, // Pass the path to the specific value (.value or .yoyGrowth)
                              rowConfig.params // Pass additional params for Utils.TableCell
                            )}
                            {ChartUtils.renderTinyChart(
                              ChartUtils.getXyDataFromValues(
                                incomeYValues,
                                rowConfig.trendChartKey // Use the trend chart key from config
                              ),
                              rowConfig.trendChartDataKey // Use the trend chart data key from config
                            )}
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  EPS is closely related to Net Profit as it is equal to Net
                  Profit/Number of Shares. <br /> Ideally both should follow the
                  same growth pattern, unless the company has diluted its equity
                  by issuing more shares.
                </Card.Content>
              </Card>
            </div>
          </Grid.Column>

          <Grid.Column width={5}>
            <div className="chart-container">
              {ChartUtils.renderObservations(
                props.title, // Assuming props.title is the stock ticker or name
                props.stockValues.epsObservations // Use epsObservations
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

export default EPSGrowthCheck;
